import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import {
  SubscriptionConstructionStore,
  useSubscriptionsConstructionStore,
} from './stores/SubscriptionsConstructionStore';
import { SubscriptionConstruction } from './types';

type SubscriptionsConstructionContextValue = {
  data: null | SubscriptionConstruction[];
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for current user subscriptions construction version.
 */
const SubscriptionsConstructionContext =
  getOrCreateGlobalContext<SubscriptionsConstructionContextValue>(
    CONTEXT_NAMES.SubscriptionsConstructionContext,
  );

const useSubscriptionsConstructionStoreWithValidation = () =>
  useStoreWithValidation<SubscriptionConstructionStore>(
    CONTEXT_NAMES.SubscriptionsConstructionContext,
    {
      hook: 'useSubscriptionsConstruction',
      provider: 'SubscriptionsConstructionProvider',
    },
  );

/**
 * Returns the current user's construction subscriptions. Fetches data when `enable` is true.
 */
export const useSubscriptionsConstruction = (enable = true) => {
  const {
    data: dataResponse,
    error,
    loading,
    retry,
    fetchData,
  } = useSubscriptionsConstructionStoreWithValidation();

  useEffect(() => {
    if (enable) {
      fetchData();
    }
  }, [enable, fetchData]);

  return {
    data: error?.status === 401 ? null : (dataResponse?.results ?? null),
    loading: enable && loading,
    error,
    retry,
    setEnabled: noop,
  };
};

export const useSubscriptionsConstructionProvider = () => {
  const {
    data: dataResponse,
    error,
    loading,
    retry,
    fetchData,
  } = useSubscriptionsConstructionStore();

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.SubscriptionsConstructionContext);
    fetchData();
  }, [fetchData]);

  const data = error?.status === 401 ? null : (dataResponse?.results ?? null);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      setEnabled: legacySetEnabled,
    }),
    [data, loading, error, retry, legacySetEnabled],
  );
};

type SubscriptionsConstructionProviderProps = {
  children?: ReactNode;
};

export const SubscriptionsConstructionProvider: ComponentType<
  SubscriptionsConstructionProviderProps
> = ({ children }) => {
  const subscriptionsConstructionProviderValue =
    useSubscriptionsConstructionProvider();

  return (
    <SubscriptionsConstructionContext.Provider
      value={subscriptionsConstructionProviderValue}
    >
      {children}
    </SubscriptionsConstructionContext.Provider>
  );
};

SubscriptionsConstructionProvider.displayName =
  'SubscriptionsConstructionProvider';
